import type {
	AbschnittCElectionResult,
	AbschnittCElectionResultCommons,
	AbschnittCElectionResultWithoutCommons
} from "@/lib/abschnittc/AbschnittCElectionResult";
import type { AbschnittCCandidateGender, AbschnittCElection, CandidateId } from "@/lib/abschnittc/AbschnittCElection";
import { arrayEquals } from "@/lib/utility";
import { AbschnittCElection19EWResultReader } from "@/lib/abschnittc/impl/AbschnittCElection19EWResultReader";

import type { EvaluatorContext } from "@/lib/abschnittc/impl/EvaluatorContext";
import {
	AbschnittCElectionListVoteResultApprovalReader
} from "@/lib/abschnittc/impl/AbschnittCElectionListVoteResultApprovalReader";
import {
	AbschnittCElectionListVoteResultPreliminaryListReader
} from "@/lib/abschnittc/impl/AbschnittCElectionListVoteResultPreliminaryListReader";
import { AbschnittCElectionEvaulationInterrupt } from "@/lib/abschnittc/impl/AbschnittCElectionEvaulationInterrupt";
import debug from "debug";
import {
	AbschnittCElectionListVoteResultRunoffCandidateReader
} from "@/lib/abschnittc/impl/AbschnittCElectionListVoteResultRunoffCandidateReader";
import { AbschnittCElectionFinalListReader } from "@/lib/abschnittc/impl/AbschnittCElectionFinalListReader";
import type { AbschnittCDirectComparisonResult } from "@/lib/abschnittc/impl/AbschnittCDirectComparison";

const logger = debug("abschnittc:AbschnittCElectionEvaluatorRun");

function toTimeStr(timeStamp: number) {
	const date = new Date(timeStamp);
	return date.toLocaleString("de-DE", { timeZone: "Europe/Berlin" });
}

export class AbschnittCElectionEvaluatorRun {

	private common: AbschnittCElectionResultCommons = {
		messages: [],
		preliminaryLists: {
			male: [],
			female: []
		},
		runoffCandidates: [],
		spots: []
	};

	constructor(private election: AbschnittCElection) {
	}

	getResult(): AbschnittCElectionResult {
		try {
			const r = this.run();
			return { ...r, ...this.common } as AbschnittCElectionResult;
		} catch (e: any) {
			if (AbschnittCElectionEvaulationInterrupt.isInterruptedError(e)) {
				return {
					...e.resultWithoutCommons,
					...this.common
				} as AbschnittCElectionResult;
			} else {
				return {
					...this.common,
					type: "error",
					message: e.stack as string
				};
			}
		}
	}

	needLeadElections() {
		return this.election.settings.leadElectionSeats > 0;
	}

	message(type: "averages", candidateId: CandidateId, score: number, total: number): void;
	message(type: "approval", candidateId: CandidateId, votes: [number, number], isApproved: boolean): void;
	message(type: "directComparisonResult", result: AbschnittCDirectComparisonResult): void;
	message(type: "minSpotMatch", spot: number, skippedCandidates: CandidateId[]): void;
	message(type: "text", text: string): void;
	message(type: string, ...args: any[]): void {
		if (type === "approval") {
			this.common.messages.push({
				type: "approval",
				candidateId: args[0] as CandidateId,
				votes: args[1] as [number, number],
				isApproved: args[2] as boolean
			});
		} else if (type === "averages") {
			this.common.messages.push({
				type: "averages",
				candidateId: args[0] as CandidateId,
				score: args[1] as number,
				total: args[2] as number
			});
		} else if (type === "text") {
			this.common.messages.push({
				type: "text", text: args[0] as string
			});
		} else if (type === "directComparisonResult") {
			this.common.messages.push({
				type: "directComparison",
				result: args[0] as AbschnittCDirectComparisonResult
			});
		} else if (type === "minSpotMatch") {
			this.common.messages.push({
				type: "minSpotMatch",
				spot: args[0] as number,
				skippedCandidates: args[1] as CandidateId[]
			});
		}
	}

	settingsAndCandidatesCheck() {

		// check if settings confirmed
		if (!this.election.settings.settingsConfirmed)
			throw new AbschnittCElectionEvaulationInterrupt({ type: "confirm-settings" });

		this.message("text", `Einstellungen der Wahl durch die Zählkommission bestätigt um ${toTimeStr(this.election.settings.settingsConfirmed)}`);

		// check if candidates confirmed
		if (!this.election.settings.candidatesConfirmed)
			throw new AbschnittCElectionEvaulationInterrupt({ type: "confirm-candidate-list" });

		this.message("text", `Kandidatenliste durch die Zählkommission bestätigt um ${toTimeStr(this.election.settings.candidatesConfirmed)}`);

	}

	private nextSpot = 1;

	getCandidateOrThrow(id: CandidateId) {
		const candidate = this.election.candidates
			.find(v => v.id === id);

		if (!candidate)
			throw new Error(`Candidate with id ${id} not found in list of ${JSON.stringify(this.election.candidates)}`);

		return candidate;
	}

	candidateNames(ids: CandidateId[]) {
		return ids.map(id => this.candidateName(id)).join(", ");
	}

	candidateName(id: CandidateId) {
		const candidate = this.getCandidateOrThrow(id);
		const words = [];

		if (candidate.title)
			words.push(candidate.title);

		words.push(candidate.firstName);
		words.push(candidate.lastName);

		if (candidate.extra)
			words.push(candidate.extra);

		return words.join(" ");
	}

	forceAcceptanceOrDenial(id: CandidateId) {
		if (this.election.acceptances[id] === undefined)
			throw new AbschnittCElectionEvaulationInterrupt({ type: "acceptance-required", candidate: id });

		if (!this.election.acceptances[id])
			throw new Error("Candidate rejected");

		this.message("text", `Bewerber*in ${this.candidateName(id)} hat die Wahl angenommen.`);
	}

	candidatesThatDidntReject() {
		const out: CandidateId[] = [];

		for (const candidate of this.election.candidates) {
			let record = this.election.acceptances[candidate.id];

			if (record === undefined || record === null || record)
				out.push(candidate.id);
			// if false, dont include
		}

		return out;
	}

	candidateHasNotRejected(id: CandidateId) {
		return this.election.acceptances[id] !== false;
	}

	candidatesThatDidntRejectOrElected() {
		return this.candidatesThatDidntReject().filter(v => !this.common.spots.includes(v));
	}

	candidatesRunningForSeat(seat: number) {
		const ids = this.candidatesThatDidntReject();

		const out: CandidateId[] = [];

		for (const id of ids) {
			const candidate = this.getCandidateOrThrow(id);

			if (seat >= candidate.minSpot)
				out.push(id);
		}

		return out;
	}

	candidatesCanStillTakeASeat(): CandidateId[] {
		const ids = this.candidatesThatDidntReject();
		return ids.filter(v => !this.common.spots.includes(v));
	}

	candidatesRunningForSeatNotYetElected(seat: number) {
		return this.candidatesRunningForSeat(seat)
			.filter(v => !this.common.spots.includes(v));
	}

	candidatesForGender(gender: AbschnittCCandidateGender) {
		const out: CandidateId[] = [];

		for (const candidate of this.election.candidates) {
			if (candidate.list === gender)
				out.push(candidate.id);
		}

		return out;
	}

	onlyOppositeGender(ids: CandidateId[], gender?: AbschnittCCandidateGender) {
		if (!gender)
			return ids;
		return ids.filter(id => this.getCandidateOrThrow(id).list !== gender);
	}

	pushNextAndQuitIfLimitReached(id: CandidateId, nextOnes: CandidateId[] = []) {

		// add to common
		this.common.spots.push(id);
		this.nextSpot++;

		// check hard limit
		if (this.election.settings.listLimit > 0) {
			if (this.nextSpot > this.election.settings.listLimit) {
				this.message("text", `Hartes Limit von ${this.election.settings.listLimit} Listenplätzen erreicht.`);
				throw new AbschnittCElectionEvaulationInterrupt({ type: "complete", endReason: "list-limit" });
			}
		}


		// check parity limit
		if (this.election.settings.listLimitParity && nextOnes.length > 0) {

			// find out the minimum number of candidates in spots + remaining for each gender
			const ids = this.common.spots.concat(nextOnes);
			const candidates = ids.map(v => this.getCandidateOrThrow(v));
			const counts = { male: 0, female: 0 };
			for (const cd of candidates) {
				counts[cd.list]++;
			}
			const minCount = Math.min(counts.female, counts.male);
			const parityLength = minCount * 2;

			if (this.nextSpot > parityLength) {
				this.message("text", `Parität erreicht. Keine weiteren Kandidat*innen der anderen Liste verfügbar.`);
				throw new AbschnittCElectionEvaulationInterrupt({ type: "complete", endReason: "parity-limit" });
			}
		}

	}

	run(): AbschnittCElectionResultWithoutCommons {

		const context: EvaluatorContext = {
			message: this.message.bind(this),
			candidateName: (id) => this.candidateName(id)
		};

		this.settingsAndCandidatesCheck();

		if (this.needLeadElections()) {

			// first check which lead elections have been performed already
			const results = this.election.leadElections;

			const previousCandidates: CandidateId[] = this.candidatesRunningForSeatNotYetElected(1);
			const runoffCandidates: CandidateId[] = [];
			let nthRunoff = 0;
			let previousGender = undefined;

			for (let i = 0; i < results.length; i++) {
				const result = results[i];

				logger(`Evaluating lead election ${i}`);
				if (!arrayEquals(result.candidateIds, previousCandidates)) {
					logger("AE:", result.candidateIds, previousCandidates);
					throw new Error("Lead election results are not in the correct order");
				}

				const reader = new AbschnittCElection19EWResultReader(result, context);

				let winner = reader.winner();

				if (winner) {
					this.message(`text`, `Bewerber*in ${this.candidateName(winner)} hat genug Stimmen erhalten und ist auf Platz ${this.nextSpot} gewäglt.`);
					this.forceAcceptanceOrDenial(winner);
					this.pushNextAndQuitIfLimitReached(winner);
					previousGender = this.getCandidateOrThrow(winner).list;
					runoffCandidates.length = 0;
					nthRunoff = 0;
					previousCandidates.length = 0;
					const runningForSeat = this.candidatesRunningForSeatNotYetElected(this.nextSpot);
					const oppositeGender = this.onlyOppositeGender(runningForSeat, previousGender);
					previousCandidates.push(...oppositeGender);
				} else {
					const nextRunoffCandidates = reader.runoffCandidates();
					this.message(`text`, `Kein*e Bewerber*in hat die absolute Mehrheit der Stimmen erreicht. Eine Stichwahl ist notwendig.`);
					this.message(`text`, `Die Kandidat*innen für die Stichwahl mit den meisten Stimmen sind: ${this.candidateNames(nextRunoffCandidates)}`);
					runoffCandidates.length = 0;
					runoffCandidates.push(...nextRunoffCandidates);
					nthRunoff++;
					previousCandidates.length = 0;
					previousCandidates.push(...nextRunoffCandidates);
				}

			}

			if (runoffCandidates.length > 0) {
				throw new AbschnittCElectionEvaulationInterrupt({
					type: "19ew-vote-required",
					candidates: runoffCandidates,
					isLead: true,
					what: `${nthRunoff}. Stichwahl für Listenplatz ` + this.nextSpot
				});
			}

			if (this.nextSpot <= this.election.settings.leadElectionSeats) {
				const runningForSeat = this.candidatesRunningForSeatNotYetElected(this.nextSpot);
				const oppositeGender = this.onlyOppositeGender(runningForSeat, previousGender);

				throw new AbschnittCElectionEvaulationInterrupt({
					type: "19ew-vote-required",
					what: `Listenplatz ${this.nextSpot}`,
					isLead: true,
					candidates: oppositeGender
				});
			}

		}

		// assert all spots that shouldve been elected by lead elections have been elected
		if (!this.election.listVotes.confirmed) {
			throw new AbschnittCElectionEvaulationInterrupt({
				type: "list-vote-required",
				candidates: this.candidatesThatDidntRejectOrElected()
			});
		}

		// now we determine preliminiary lists
		const maleCandidates = this.candidatesForGender("male")
			.filter(v => !this.common.spots.includes(v));
		const femaleCandidates = this.candidatesForGender("female")
			.filter(v => !this.common.spots.includes(v));


		const maleListApprovalReader = new AbschnittCElectionListVoteResultApprovalReader({
			gender: "male", candidates: maleCandidates, vote: this.election.listVotes, context
		});
		const femaleListApprovalReader = new AbschnittCElectionListVoteResultApprovalReader({
			gender: "female", candidates: femaleCandidates, vote: this.election.listVotes, context
		});

		const approvedMaleCandidateIds = maleListApprovalReader.approvedCandidates();
		const approvedFemaleCandidateIds = femaleListApprovalReader.approvedCandidates();

		this.message("text", `Es wurden insgesamt ${approvedMaleCandidateIds.length} von ${maleCandidates.length} ` +
			`männlichen Bewerber*innen und ${approvedFemaleCandidateIds.length} von ${femaleCandidates.length} weiblichen Bewerber*innen zugelassen.`);

		// if we have two non-empty lists
		if (approvedMaleCandidateIds.length > 0 && approvedFemaleCandidateIds.length > 0) {

			// make preliminary lists
			const maleListReader = new AbschnittCElectionListVoteResultPreliminaryListReader({
				gender: "male",
				lots: this.election.lotResults,
				candidates: approvedMaleCandidateIds,
				vote: this.election.listVotes,
				context
			});
			const femaleListReader = new AbschnittCElectionListVoteResultPreliminaryListReader({
				gender: "female",
				lots: this.election.lotResults,
				candidates: [...approvedFemaleCandidateIds],
				vote: this.election.listVotes,
				context
			});

			// get preliminary lists
			this.common.preliminaryLists.male = maleListReader.list();
			this.common.preliminaryLists.female = femaleListReader.list();

			// do we need runoff?
			if (this.nextSpot === 1) {
				// if yes determine runoff candidates
				this.message("text", `Es wird eine Stichwahl für den ersten Listenplatz benötigt.`);

				const maleApprovedCandidates = approvedMaleCandidateIds.map(v => this.getCandidateOrThrow(v));
				const femaleApprovedCandidates = approvedFemaleCandidateIds.map(v => this.getCandidateOrThrow(v));

				logger("maleApprovedCandidates", approvedMaleCandidateIds, maleApprovedCandidates);
				logger("femaleApprovedCandidates", approvedFemaleCandidateIds, femaleApprovedCandidates);

				const maleRunoffReader = new AbschnittCElectionListVoteResultRunoffCandidateReader({
					gender: "male",
					preliminaryResults: this.common.preliminaryLists.male,
					vote: this.election.listVotes,
					candidates: maleApprovedCandidates,
					context
				});
				const femaleRunoffReader = new AbschnittCElectionListVoteResultRunoffCandidateReader({
					gender: "female",
					preliminaryResults: this.common.preliminaryLists.female,
					vote: this.election.listVotes,
					candidates: femaleApprovedCandidates,
					context
				});

				const maleRunoff = maleRunoffReader.runoffCandidate();
				const femaleRunoff = femaleRunoffReader.runoffCandidate();

				this.message("text", `Die Kandidat*innen für die Stichwahl um den ersten Listenplatz sind: ` +
					`${this.candidateName(maleRunoff)} und ${this.candidateName(femaleRunoff)}`);

				if (this.election.runoffResults.length === 0) {
					throw new AbschnittCElectionEvaulationInterrupt({
						type: "19ew-vote-required",
						what: "2. Wahlgang zur Bestimmung des Spitzenplatzes",
						candidates: [maleRunoff, femaleRunoff],
						isLead: false
					});
				} else {

					let i = 0;
					let winner = undefined;

					for (i = 0; i < this.election.runoffResults.length; i++) {
						const result = this.election.runoffResults[i];

						const reader = new AbschnittCElection19EWResultReader(result, context);

						winner = reader.winner();
					}

					if (winner) {
						this.message(`text`, `Bewerber*in ${this.candidateName(winner)} hat genug Stimmen erhalten und ist auf Platz ${this.nextSpot} gewäglt.`);
						this.forceAcceptanceOrDenial(winner);
						this.pushNextAndQuitIfLimitReached(winner);
					} else {
						throw new AbschnittCElectionEvaulationInterrupt({
							type: "19ew-vote-required",
							what: `${i}. Stichwahl zum 2. Wahlgang zur Bestimmung des Spitzenplatzes`,
							candidates: [maleRunoff, femaleRunoff],
							isLead: false
						});
					}
				}
			} else {
				// if no, determine the final list
				this.message("text", `Da bereits einzelne Plätze per Einzelwahl vergeben wurden, wird keine Stichwahl benötigt.`);

			}

			const candidatesRemainingForList = [...approvedMaleCandidateIds, ...approvedFemaleCandidateIds]
				.filter(v => this.candidateHasNotRejected(v))
				.map(v => this.getCandidateOrThrow(v));

			let notYetElected = [...approvedMaleCandidateIds, ...approvedFemaleCandidateIds]
				.filter(v => !this.common.spots.includes(v))
				.filter(v => this.candidateHasNotRejected(v));

			const allCandidates = [...this.common.spots
				.map(v => this.getCandidateOrThrow(v)), ...candidatesRemainingForList];

			while (notYetElected.length > 0) {

				const finalListReader = new AbschnittCElectionFinalListReader({
					elected: this.common.spots,
					candidates: allCandidates,
					context,
					acceptances: this.election.acceptances,
					preliminaryResults: {
						male: this.common.preliminaryLists.male.filter(v => this.candidateHasNotRejected(v.candidate)),
						female: this.common.preliminaryLists.female.filter(v => this.candidateHasNotRejected(v.candidate))
					}
				});

				logger("notYetElected", notYetElected);

				const final = finalListReader.nextCandidate();

				if (!final) {
					throw new Error(`No final candidate found: ${notYetElected.map(v => this.candidateName(v)).join(", ")}`);
				}

				notYetElected.splice(notYetElected.indexOf(final), 1);
				this.pushNextAndQuitIfLimitReached(final, notYetElected);

			}

			throw new AbschnittCElectionEvaulationInterrupt({
				type: "complete",
				endReason: "no-candidates"
			});


		} else if (approvedMaleCandidateIds.length > 0) {

		} else if (approvedFemaleCandidateIds.length > 0) {

		}


		throw new Error("Not implemented");
	}
}
